import { create } from "zustand";
import $api from "@/zapi-frontend/shared/api/axios";

export const POSTHOG_COOKIE_KEY = "ph_phc_*_posthog";

type TCookieConsent = {
  hasConsented: boolean;
  cookies: Record<string, boolean>;
};

async function fetchConsentFromApi(): Promise<TCookieConsent | null> {
  try {
    const response = await $api.get<{ data: TCookieConsent | null }>("/user/cookie-consent");
    return response.data?.data ?? null;
  } catch {
    return null;
  }
}

export function isCookieAllowed(cookieKey: string, _userId: string | number | undefined): boolean {
  const { cookies } = useCookieConsentStore.getState();
  return cookies[cookieKey] ?? true;
}

type TCookieConsentState = {
  showConsentModal: boolean;
  showConfigureView: boolean;
  userId: string | number | undefined;
  hasConsented: boolean;
  cookies: Record<string, boolean>;
  openModal: () => void;
  openConfigureModal: () => void;
  closeModal: () => void;
  setUserId: (userId: string | number) => Promise<void>;
  setCookieEnabled: (key: string, enabled: boolean) => void;
  savePreferences: (cookies: Record<string, boolean>) => void;
  hasGivenConsent: () => boolean;
};

export const useCookieConsentStore = create<TCookieConsentState>((set, get) => ({
  showConsentModal: false,
  showConfigureView: false,
  userId: undefined,
  hasConsented: false,
  cookies: { [POSTHOG_COOKIE_KEY]: true },
  openModal: () => set({ showConsentModal: true, showConfigureView: false }),
  openConfigureModal: () => set({ showConsentModal: true, showConfigureView: true }),
  closeModal: () => set({ showConsentModal: false, showConfigureView: false }),
  setUserId: async (userId) => {
    const stored = await fetchConsentFromApi();
    set({
      userId,
      hasConsented: stored?.hasConsented ?? false,
      cookies: stored?.cookies ?? { [POSTHOG_COOKIE_KEY]: true }
    });
  },
  setCookieEnabled: (key, enabled) => set((state) => ({ cookies: { ...state.cookies, [key]: enabled } })),
  savePreferences: (cookies) => {
    set({ cookies, hasConsented: true });
    $api.post("/user/cookie-consent", { hasConsented: true, cookies }).catch(() => { void 0; });
  },
  hasGivenConsent: () => {
    return get().hasConsented;
  }
}));
